import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Calendar,
  RefreshCw,
  DollarSign,
  ShoppingBag,
  Users,
  TrendingUp,
  BarChart3,
  PieChart as PieChartIcon,
  Receipt,
  BadgeCheck,
  Trophy,
  Clock,
  CreditCard,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { extractBusinessType, getStoredBusinessType } from "../utils/businessType";
import { apiFetch } from "../utils/apiClient";
import { getUserFacingError } from "../utils/userFacingError";
import StockSituationPanel from "./StockSituationPanel";

const API_BASE = "/api";
const formatCurrency = (value = 0) =>
  `${new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value) || 0)} FCFA`;
const formatNumber = (value = 0) =>
  new Intl.NumberFormat("fr-FR").format(Number(value) || 0);
const compactNumberFormatter = new Intl.NumberFormat("fr-FR", {
  notation: "compact",
  compactDisplay: "short",
  maximumFractionDigits: 1,
});
const formatCompactNumber = (value = 0) =>
  compactNumberFormatter.format(Number(value) || 0);
const formatTooltipValue = (value, _name, props) => {
  const key = props?.dataKey || "";
  if (key === "sales" || key === "revenue" || key === "value") {
    return formatCurrency(value);
  }
  return formatNumber(value);
};
const getChartMinWidth = (length = 0, baseWidth = 520) => {
  const count = Number(length) || 0;
  if (!count) return baseWidth;
  const step = count > 120 ? 24 : count > 60 ? 36 : 56;
  return Math.max(baseWidth, count * step);
};

const QUICK_RANGES = [
  { id: "7d", label: "7 j", days: 7 },
  { id: "15d", label: "15 j", days: 15 },
  { id: "30d", label: "30 j", days: 30 },
  { id: "90d", label: "90 j", days: 90 },
];

const PIE_COLORS = [
  "#f97316",
  "#0ea5e9",
  "#a855f7",
  "#22c55e",
  "#facc15",
  "#ec4899",
  "#14b8a6",
  "#8b5cf6",
];

const REPORTS_RANGE_STORAGE_KEY = "moo_reports_range";

const Reports = () => {
  const defaultEnd = new Date();
  const defaultStart = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const todayDate = new Date().toISOString().split("T")[0];
  const [businessType, setBusinessType] = useState(getStoredBusinessType);
  const isBoutique = businessType === "boutique";

  const readStoredRange = () => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem(REPORTS_RANGE_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      const isValid = (value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
      if (!isValid(parsed.startDate) || !isValid(parsed.endDate)) return null;
      const storedServiceDay = isValid(parsed.serviceDayDate) ? parsed.serviceDayDate : null;
      return {
        startDate: parsed.startDate,
        endDate: parsed.endDate,
        activeRange: parsed.activeRange,
        serviceDayDate: storedServiceDay,
      };
    } catch (_e) {
      return null;
    }
  };

  const storedRange = readStoredRange();
  const defaultStartDate = defaultStart.toISOString().split("T")[0];
  const defaultEndDate = defaultEnd.toISOString().split("T")[0];
  const allowedRanges = new Set([
    ...QUICK_RANGES.map((range) => range.id),
    "all",
    "service",
    "custom",
  ]);
  const normalizedActiveRange = allowedRanges.has(storedRange?.activeRange)
    ? storedRange?.activeRange
    : "custom";

  const [startDate, setStartDate] = useState(storedRange?.startDate || defaultStartDate);
  const [endDate, setEndDate] = useState(storedRange?.endDate || defaultEndDate);
  const [activeRange, setActiveRange] = useState(storedRange ? normalizedActiveRange : "30d");
  const [serviceDayRange, setServiceDayRange] = useState(null);
  const [serviceDayDate, setServiceDayDate] = useState(
    storedRange?.serviceDayDate || todayDate,
  );
  const [loadingServiceDay, setLoadingServiceDay] = useState(false);

  const [analytics, setAnalytics] = useState(null);

  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [dailyStockSituation, setDailyStockSituation] = useState(null);
  const getPreferredDark = () => {
    if (typeof document === "undefined") return false;
    const docHasDark = document.documentElement.classList.contains("dark");
    const stored = typeof localStorage !== "undefined" ? localStorage.getItem("theme") : null;
    const media = typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
      : false;
    return stored ? stored === "dark" : docHasDark || media;
  };
  const [isDarkMode, setIsDarkMode] = useState(getPreferredDark);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const target = document.documentElement;
    const syncTheme = () => setIsDarkMode(getPreferredDark());
    const observer = new MutationObserver(syncTheme);
    observer.observe(target, { attributes: true, attributeFilter: ["class"] });
    syncTheme();
    const media = window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null;
    media?.addEventListener("change", syncTheme);
    return () => {
      observer.disconnect();
      media?.removeEventListener("change", syncTheme);
    };
  }, []);

  useEffect(() => {
    const loadBusinessType = async () => {
      try {
        const res = await fetch("/api/restaurant-info");
        if (res.ok) {
          const data = await res.json();
          setBusinessType(extractBusinessType(data));
          if (typeof localStorage !== "undefined") {
            localStorage.setItem("restaurant-info", JSON.stringify(data || {}));
          }
          return;
        }
      } catch (_error) {
        // ignore and fallback to cache
      }
      try {
        const cached = localStorage.getItem("restaurant-info");
        if (cached) {
          const parsed = JSON.parse(cached);
          setBusinessType(extractBusinessType(parsed));
        }
      } catch (_error) {
        // ignore
      }
    };
    loadBusinessType();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const payload = {
      startDate,
      endDate,
      activeRange,
      serviceDayDate,
    };
    window.localStorage.setItem(REPORTS_RANGE_STORAGE_KEY, JSON.stringify(payload));
  }, [startDate, endDate, activeRange, serviceDayDate]);

  const fetchAnalytics = useCallback(async () => {
    try {
      if (activeRange === "service" && (!serviceDayRange?.start || !serviceDayRange?.end)) {
        setLoadingAnalytics(false);
        return;
      }
      setLoadingAnalytics(true);
      setAnalyticsError(null);
      const params = new URLSearchParams();
      if (activeRange === "all") {
        params.set("scope", "all");
      } else if (
        activeRange === "service" &&
        serviceDayRange?.start &&
        serviceDayRange?.end
      ) {
        params.set("startDate", serviceDayRange.start);
        params.set("endDate", serviceDayRange.end);
      } else {
        params.set("startDate", startDate);
        params.set("endDate", endDate);
      }
      const response = await fetch(`${API_BASE}/reports/analytics?${params}`);
      if (!response.ok) throw new Error("Impossible de charger les statistiques");
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error("❌ Rapports analytics:", error);
      setAnalyticsError(getUserFacingError(error, "Statistiques momentanément indisponibles."));
    } finally {
      setLoadingAnalytics(false);
    }
  }, [activeRange, endDate, serviceDayRange, startDate]);

  const fetchServiceDayRange = useCallback(async (targetDate) => {
    try {
      if (!targetDate) return;
      setLoadingServiceDay(true);
      const params = new URLSearchParams({ date: targetDate });
      const response = await fetch(`${API_BASE}/service-day/range?${params}`);
      if (!response.ok) throw new Error("Impossible de charger la journée de service");
      const data = await response.json();
      setServiceDayRange(data?.range || null);
    } catch (error) {
      console.error("❌ Journée de service:", error);
    } finally {
      setLoadingServiceDay(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    if (activeRange !== "service") return;
    if (!serviceDayDate || loadingServiceDay) return;
    fetchServiceDayRange(serviceDayDate);
  }, [activeRange, fetchServiceDayRange, loadingServiceDay, serviceDayDate]);

  useEffect(() => {
    let active = true;
    if (activeRange !== "service" || !serviceDayDate) {
      setDailyStockSituation(null);
      return () => { active = false; };
    }
    apiFetch(`/api/waise/v2/daily-situation?date=${encodeURIComponent(serviceDayDate)}`, {
      cache: "no-store",
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload?.error || "Situation indisponible.");
        if (active) setDailyStockSituation(payload?.enabled ? payload : null);
      })
      .catch((error) => {
        console.warn("Situation du stock indisponible:", getUserFacingError(error));
        if (active) setDailyStockSituation(null);
      });
    return () => { active = false; };
  }, [activeRange, serviceDayDate]);

  const handleQuickRange = (range) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (range.days - 1));
    setStartDate(start.toISOString().split("T")[0]);
    setEndDate(end.toISOString().split("T")[0]);
    setActiveRange(range.id);
  };

  const handleAllRange = () => {
    setActiveRange("all");
  };

  const handleServiceRange = () => {
    setActiveRange("service");
    if (serviceDayDate && !loadingServiceDay) {
      fetchServiceDayRange(serviceDayDate);
    }
  };

  const handleServiceDayChange = (value) => {
    setServiceDayDate(value);
    setActiveRange("service");
  };

  const handleDateChange = (type, value) => {
    if (type === "start") {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
    setActiveRange("custom");
  };

  const refreshAll = async () => {
    setIsRefreshing(true);
    try {
      await fetchAnalytics();
    } finally {
      setIsRefreshing(false);
    }
  };

  const overview = analytics?.overview || {};
  const salesData = analytics?.salesData || [];
  const productPerformance = analytics?.productPerformance || [];
  const categoryDistribution = analytics?.categoryDistribution || [];
  const hourlySales = analytics?.hourlySales || [];
  const topProducts = analytics?.topProducts || [];
  const topCategories = analytics?.topCategories || [];
  const serverRanking = analytics?.serverRanking || [];
  const paymentTotals = (analytics?.paymentTotals || []).filter(
    (method) => Number(method?.total || 0) > 0,
  );
  const salesChartWidth = useMemo(
    () => getChartMinWidth(salesData.length),
    [salesData.length],
  );
  const hourlyChartWidth = useMemo(
    () => getChartMinWidth(hourlySales.length),
    [hourlySales.length],
  );

  const summaryCards = useMemo(() => {
    const cards = [
      {
        label: "Chiffre d'affaires",
        value: formatCurrency(overview.totalRevenue),
        helper: "Cumul période",
        icon: DollarSign,
        accent: "from-amber-400/20 to-orange-500/20",
      },
      {
        label: "Services",
        value: formatNumber(overview.totalOrders || 0),
        helper: "Services payés",
        icon: ShoppingBag,
        accent: "from-sky-400/20 to-blue-500/20",
      },
      {
        label: "Visiteurs",
        value: formatNumber(overview.totalVisitors ?? overview.totalCustomers ?? 0),
        helper: "Sur place",
        icon: Users,
        accent: "from-emerald-400/20 to-green-500/20",
      },
      {
        label: "Panier moyen",
        value: formatCurrency(overview.averageOrderValue),
        helper: "Valeur TTC",
        icon: TrendingUp,
        accent: "from-purple-400/20 to-indigo-500/20",
      },
    ];
    return isBoutique ? cards.filter((card) => card.label !== "Visiteurs") : cards;
  }, [overview, isBoutique]);


  if (loadingAnalytics && !analytics && !analyticsError) {
    return (
      <div
        className={`min-h-screen ${
          isDarkMode
            ? "bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950"
            : "bg-gradient-to-b from-slate-50 via-white to-slate-100"
        }`}
      >
        <div className="max-w-5xl mx-auto px-6 py-20 text-center space-y-4">
          <div className="mx-auto h-14 w-14 border-4 border-amber-400 border-b-transparent rounded-full animate-spin" />
          <p className="text-lg font-semibold">Chargement des rapports...</p>
          <p className="text-sm text-slate-500">
            Agrégation des ventes, sessions de caisse et produits en cours.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${
        isDarkMode
          ? "bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100"
          : "bg-gradient-to-b from-slate-50 via-white to-slate-100 text-slate-900"
      }`}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-8 space-y-6 sm:space-y-8">
        <section className={`${cardBg(isDarkMode)} rounded-3xl p-4 sm:p-6 shadow-xl space-y-4 sm:space-y-6`}>
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div>
            <p className="text-xs uppercase tracking-[0.4em] text-amber-300">Tableau des rapports</p>
            <h1 className="text-3xl font-bold mt-2">Statistiques</h1>
            </div>
            <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={refreshAll}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl border border-white/10 text-sm font-semibold hover:border-amber-400/60"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`} />
              Actualiser
            </button>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:flex-wrap sm:overflow-visible sm:pb-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <button
                  type="button"
                  onClick={handleAllRange}
                  className={`shrink-0 px-4 py-2 rounded-2xl text-sm font-semibold transition ${
                    activeRange === "all"
                      ? "bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow"
                      : isDarkMode
                        ? "bg-white/5 text-white/70 border border-white/10"
                        : "bg-white text-slate-600 border border-slate-200"
                  }`}
                >
                  Tout
                </button>
                {QUICK_RANGES.map((range) => (
                  <button
                    key={range.id}
                    type="button"
                    onClick={() => handleQuickRange(range)}
                    className={`shrink-0 px-4 py-2 rounded-2xl text-sm font-semibold transition ${
                      activeRange === range.id
                        ? "bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow"
                        : isDarkMode
                          ? "bg-white/5 text-white/70 border border-white/10"
                          : "bg-white text-slate-600 border border-slate-200"
                    }`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
              <div className="hidden lg:flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveRange("custom")}
                  className={`px-4 py-2 rounded-2xl text-sm font-semibold transition ${
                    activeRange !== "service"
                      ? "bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow"
                      : isDarkMode
                        ? "bg-white/5 text-white/70 border border-white/10"
                        : "bg-white text-slate-600 border border-slate-200"
                  }`}
                >
                  Plage
                </button>
                <button
                  type="button"
                  onClick={handleServiceRange}
                  className={`px-4 py-2 rounded-2xl text-sm font-semibold transition ${
                    activeRange === "service"
                      ? "bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow"
                      : isDarkMode
                        ? "bg-white/5 text-white/70 border border-white/10"
                        : "bg-white text-slate-600 border border-slate-200"
                  }`}
                >
                  Journée
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 lg:hidden">
                <button
                  type="button"
                  onClick={() => setActiveRange("custom")}
                  className={`px-4 py-2 rounded-2xl text-sm font-semibold transition ${
                    activeRange !== "service"
                      ? "bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow"
                      : isDarkMode
                        ? "bg-white/5 text-white/70 border border-white/10"
                        : "bg-white text-slate-600 border border-slate-200"
                  }`}
                >
                  Plage
                </button>
                <button
                  type="button"
                  onClick={handleServiceRange}
                  className={`px-4 py-2 rounded-2xl text-sm font-semibold transition ${
                    activeRange === "service"
                      ? "bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow"
                      : isDarkMode
                        ? "bg-white/5 text-white/70 border border-white/10"
                        : "bg-white text-slate-600 border border-slate-200"
                  }`}
                >
                  Journée
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-4 lg:ml-auto w-full lg:w-auto">
                {activeRange === "service" ? (
                  <label className="flex flex-col text-sm gap-2 w-full sm:w-auto">
                    <span className="flex items-center gap-2 opacity-70">
                      <Calendar className="w-4 h-4" />
                      Jour
                    </span>
                    <input
                      type="date"
                      value={serviceDayDate}
                      onChange={(e) => handleServiceDayChange(e.target.value)}
                      className={`w-full rounded-2xl border px-4 py-2 ${
                        isDarkMode ? "bg-white/5 border-white/10" : "bg-white border-slate-200"
                      }`}
                    />
                  </label>
                ) : (
                  <div className="grid grid-cols-2 gap-2 w-full sm:flex sm:flex-wrap sm:gap-4 sm:w-auto">
                    <label className="flex flex-col text-sm gap-2 min-w-0 w-full">
                      <span className="flex items-center gap-2 opacity-70">
                        <Calendar className="w-4 h-4" />
                        Date de début
                      </span>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => handleDateChange("start", e.target.value)}
                        className={`w-full min-w-0 max-w-full rounded-2xl border px-3 py-2 text-sm sm:px-4 sm:text-base ${
                          isDarkMode ? "bg-white/5 border-white/10" : "bg-white border-slate-200"
                        }`}
                      />
                    </label>
                    <label className="flex flex-col text-sm gap-2 min-w-0 w-full">
                      <span className="flex items-center gap-2 opacity-70">
                        <Calendar className="w-4 h-4" />
                        Date de fin
                      </span>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => handleDateChange("end", e.target.value)}
                        className={`w-full min-w-0 max-w-full rounded-2xl border px-3 py-2 text-sm sm:px-4 sm:text-base ${
                          isDarkMode ? "bg-white/5 border-white/10" : "bg-white border-slate-200"
                        }`}
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {analyticsError && (
          <div className="border border-rose-400/30 rounded-2xl p-4 bg-rose-500/10 text-sm text-rose-200">
            {analyticsError}
          </div>
        )}


        <section
          className={`grid grid-cols-1 sm:grid-cols-2 ${
            isBoutique ? "xl:grid-cols-3" : "xl:grid-cols-4"
          } gap-3 sm:gap-4`}
        >
          {summaryCards.map((card) => (
            <SummaryCard key={card.label} {...card} isDarkMode={isDarkMode} />
          ))}
        </section>

        {activeRange === "service" ? <StockSituationPanel
          initialData={dailyStockSituation}
          date={serviceDayDate}
          isDarkMode={isDarkMode}
        /> : null}

        <section className={`${cardBg(isDarkMode)} rounded-3xl p-4 sm:p-6 shadow-lg`}>
          <div className="flex items-center gap-3 mb-4">
            <span className="p-3 rounded-2xl bg-amber-400/10 text-amber-200">
              <CreditCard className="w-5 h-5" />
            </span>
            <div>
              <h2 className="text-lg font-semibold">Montants par mode de paiement</h2>
              <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                Période sélectionnée
              </p>
            </div>
          </div>
          {paymentTotals.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {paymentTotals.map((method) => (
                <div
                  key={`${method.id || method.code}-${method.name}`}
                  className="rounded-2xl border border-white/10 p-4"
                  style={{
                    background: method.color
                      ? `${method.color}1a`
                      : isDarkMode
                        ? "rgba(255,255,255,0.03)"
                        : "rgba(15,23,42,0.02)",
                  }}
                >
                  <p className="text-sm font-semibold opacity-80">{method.name}</p>
                  <p className="text-xl font-bold mt-2">{formatCurrency(method.total)}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm opacity-60">
              Aucun paiement encaissé pour cette période.
            </p>
          )}
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <ChartPanel
            title="Évolution des ventes quotidiennes"
            icon={BarChart3}
            isDarkMode={isDarkMode}
            contentClassName="-mx-2 sm:mx-0"
          >
            <div className="overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="h-72 sm:h-80 px-2 sm:px-0" style={{ minWidth: salesChartWidth }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={salesData} margin={{ top: 10, right: 24, left: -6, bottom: 0 }}>
                    <defs>
                      <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f97316" stopOpacity={0.6} />
                        <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="4 4"
                      stroke={isDarkMode ? "rgba(148,163,184,0.15)" : "rgba(15,23,42,0.08)"}
                    />
                    <XAxis dataKey="date" stroke="currentColor" tickMargin={8} />
                    <YAxis stroke="currentColor" tickFormatter={formatCompactNumber} tickMargin={8} />
                    <Tooltip
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{
                        background: isDarkMode ? "#0f172a" : "#fff",
                        borderRadius: "1rem",
                        border: "1px solid rgba(148,163,184,0.2)",
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: "0.75rem", opacity: 0.8 }} />
                    <Area
                      type="monotone"
                      dataKey="sales"
                      name="Ventes"
                      stroke="#f97316"
                      strokeWidth={2}
                      fill="url(#salesGradient)"
                      activeDot={{ r: 4 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </ChartPanel>
          <ChartPanel
            title="Performance des produits"
            icon={Receipt}
            isDarkMode={isDarkMode}
          >
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={productPerformance} margin={{ top: 12, right: 24, left: -6, bottom: 0 }}>
                <CartesianGrid
                  strokeDasharray="4 4"
                  stroke={isDarkMode ? "rgba(148,163,184,0.15)" : "rgba(15,23,42,0.08)"}
                />
                <XAxis dataKey="name" stroke="currentColor" tickMargin={8} />
                <YAxis stroke="currentColor" tickFormatter={formatCompactNumber} tickMargin={8} />
                <Tooltip
                  formatter={formatTooltipValue}
                  contentStyle={{
                    background: isDarkMode ? "#0f172a" : "#fff",
                    borderRadius: "1rem",
                    border: "1px solid rgba(148,163,184,0.2)",
                  }}
                />
                <Legend wrapperStyle={{ fontSize: "0.75rem", opacity: 0.8 }} />
                <Bar dataKey="orders" name="Commandes" fill="#0ea5e9" radius={[8, 8, 0, 0]} />
                <Bar dataKey="revenue" name="Chiffre d'affaires" fill="#f97316" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartPanel>
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <ChartPanel title="Répartition par catégorie" icon={PieChartIcon} isDarkMode={isDarkMode}>
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={categoryDistribution}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={52}
                  outerRadius={110}
                  paddingAngle={2}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {categoryDistribution.map((entry, index) => (
                    <Cell key={`cell-${entry.name}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </ChartPanel>
          <ChartPanel
            title="Ventes par heure de service"
            icon={Clock}
            isDarkMode={isDarkMode}
            contentClassName="-mx-2 sm:mx-0"
          >
            <div className="overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="h-72 sm:h-80 px-2 sm:px-0" style={{ minWidth: hourlyChartWidth }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={hourlySales} margin={{ top: 10, right: 24, left: -6, bottom: 0 }}>
                    <defs>
                      <linearGradient id="hourGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.6} />
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="4 4"
                      stroke={isDarkMode ? "rgba(148,163,184,0.15)" : "rgba(15,23,42,0.08)"}
                    />
                    <XAxis dataKey="hour" stroke="currentColor" tickMargin={8} />
                    <YAxis stroke="currentColor" tickFormatter={formatCompactNumber} tickMargin={8} />
                    <Tooltip
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{
                        background: isDarkMode ? "#0f172a" : "#fff",
                        borderRadius: "1rem",
                        border: "1px solid rgba(148,163,184,0.2)",
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: "0.75rem", opacity: 0.8 }} />
                    <Area
                      type="monotone"
                      dataKey="sales"
                      name="Ventes"
                      stroke="#22c55e"
                      strokeWidth={2}
                      fill="url(#hourGradient)"
                      activeDot={{ r: 4 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </ChartPanel>
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <TopList
            title="Top produits"
            icon={BadgeCheck}
            items={topProducts}
            isDarkMode={isDarkMode}
            renderMeta={(item) => {
              const salesCount = Number(item.sales_qty ?? item.orders ?? 0);
              return (
                <>
                  <p className="font-semibold">{formatCurrency(item.revenue)}</p>
                  <p className="text-xs opacity-70">
                    {formatNumber(salesCount)} ventes · {item.category || "Sans catégorie"}
                  </p>
                </>
              );
            }}
          />
          <TopList
            title="Top catégories"
            icon={PieChartIcon}
            items={topCategories}
            isDarkMode={isDarkMode}
            renderMeta={(item) => {
              const salesCount = Number(item.sales_qty ?? item.orders ?? 0);
              return (
                <>
                  <p className="font-semibold">{formatCurrency(item.revenue)}</p>
                  <p className="text-xs opacity-70">
                    {formatNumber(item.products || 0)} produits · {formatNumber(salesCount)} ventes
                  </p>
                </>
              );
            }}
          />
        </section>

        <section>
          <TopList
            title="Classement des serveurs"
            icon={Trophy}
            items={serverRanking}
            isDarkMode={isDarkMode}
            renderMeta={(item) => (
              <>
                <p className="font-semibold">{formatCurrency(item.totalSales)}</p>
                <p className="text-xs opacity-70">
                  {formatNumber(item.services || 0)} commande{Number(item.services || 0) > 1 ? "s" : ""}
                </p>
              </>
            )}
          />
        </section>

      </div>
    </div>
  );
};

const cardBg = (isDark) =>
  isDark ? "bg-[#0f172a]/80 border border-white/10" : "bg-white border border-slate-100";

const SummaryCard = ({ label, value, helper, icon: Icon, accent, isDarkMode }) => (
  <div className={`${cardBg(isDarkMode)} rounded-3xl p-5 shadow-lg`}>
    <div className="flex items-center justify-between mb-3">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-amber-300">{label}</p>
        <p className="text-2xl font-bold mt-1">{value}</p>
      </div>
      <span className={`p-3 rounded-2xl bg-gradient-to-br ${accent}`}>
        <Icon className="w-5 h-5" />
      </span>
    </div>
    <p className="text-sm opacity-70">{helper}</p>
  </div>
);

const ChartPanel = ({ title, icon: Icon, children, isDarkMode, contentClassName }) => (
  <div className={`${cardBg(isDarkMode)} rounded-3xl p-4 sm:p-6 shadow-lg`}>
    <div className="flex items-center gap-3 mb-4">
      <span className="p-3 rounded-2xl bg-amber-400/10 text-amber-200">
        <Icon className="w-5 h-5" />
      </span>
      <div>
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Analyse</p>
      </div>
    </div>
    <div className={contentClassName}>{children}</div>
  </div>
);

const TopList = ({ title, icon: Icon, items, renderMeta, isDarkMode }) => (
  <div className={`${cardBg(isDarkMode)} rounded-3xl p-4 sm:p-6 shadow-lg`}>
    <div className="flex items-center gap-3 mb-4">
      <span className="p-3 rounded-2xl bg-emerald-400/15 text-emerald-200">
        <Icon className="w-5 h-5" />
      </span>
      <div>
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Classement</p>
      </div>
    </div>
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="text-sm opacity-60">Aucune donnée disponible pour cette période.</p>
      ) : (
        items.map((item, index) => (
          <div
            key={`${item.name}-${index}`}
            className="rounded-2xl border border-white/10 px-4 py-3 bg-white/5"
          >
            <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-center">
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 rounded-2xl bg-amber-400/20 text-amber-100 flex items-center justify-center font-semibold">
                  {index + 1}
                </span>
                <div className="min-w-0">
                  <p className="font-semibold text-base sm:text-sm">{item.name}</p>
                </div>
              </div>
              <div className="text-sm sm:text-right sm:justify-self-end">
                {renderMeta(item)}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);

export default Reports;
